/**
 * Campanhas — o guarda-chuva da aquisição.
 *
 * Quatro decisões que valem o comentário:
 *
 *  - **O código aparece, e aparece como código.** É a chave curta que vai no anúncio e no
 *    relatório, então ela precisa ser lida e copiada sem erro: fonte monoespaçada, sempre
 *    em maiúscula, e nunca dentro de um campo que pareça editável — porque não é.
 *  - **"Vinculados" não é "podem receber".** Uma campanha com 3 grupos em que 2 estão
 *    cheios distribui para 1. A lista mostra a contagem, e a campanha ativa sem nenhum
 *    grupo ganha um aviso próprio: é a causa número um de "criei e não aconteceu nada".
 *  - **Arquivar e excluir são dois botões, não um.** Arquivar volta; excluir não. E excluir
 *    só aparece na campanha já arquivada — a mesma ordem que o servidor exige.
 *  - **Excluir diz o que NÃO acontece.** A pergunta real de quem clica é "perco o
 *    histórico?", e a resposta é não. Dizer isso na hora evita a dúvida que trava a ação.
 *
 * No celular a tabela vira lista de cartões: `campanhas` está na barra inferior, então é
 * tela que se olha em pé, com uma mão (DESIGN_SYSTEM §11).
 */
import { useCallback, useMemo, useState } from 'react';
import {
  ActionMenu,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  ErrorState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  TableSkeleton,
  TableEntity,
  Tabs,
  TabsList,
  TabsTrigger,
  useToast,
} from '@mgdlead/ui';
import type { ActionMenuItem } from '@mgdlead/ui';
import {
  Archive,
  ArchiveRestore,
  Megaphone,
  Pause,
  Pencil,
  Play,
  Plus,
  Target,
  Trash2,
  Users,
} from 'lucide-react';
import { can } from '@mgdlead/shared';

import { FormAlert } from '@mgdlead/ui';
import { PageHeader } from '@mgdlead/ui';
import { CampaignFormDialog } from './CampaignFormDialog';
import { CampaignGroupsDrawer } from './CampaignGroupsDrawer';
import { CampaignMetaDrawer } from './CampaignMetaDrawer';
import {
  CAMPAIGN_STATUS,
  groupsSummary,
  hasMetaLink,
  isDistributingWithoutGroups,
} from '../../../features/campaign/campaign-ui';
import { CampaignGoalLight } from '../../../features/goals/CampaignGoalLight';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { workspacePath } from '../../../features/workspace/navigation';
import {
  useArchiveCampaign,
  useCampaigns,
  useCreateCampaign,
  useLinkCampaignGroup,
  useLinkCampaignMeta,
  useRemoveCampaign,
  useUnarchiveCampaign,
  useUnlinkCampaignGroup,
  useUnlinkCampaignMeta,
  useUpdateCampaign,
  useAnalyticsBreakdown,
  useCampaignCards,
} from '../../../lib/api';
import type { Campaign, CampaignLimit } from '../../../lib/api';
import { formatExact, formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';
import { runInSequence } from '../../../lib/sequential';
import { useOpenFromParam } from '../../../features/experience/useOpenFromParam';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import {
  CampaignPerformanceCard,
  CampaignTotals,
  MetaFreshness,
} from '../../../features/campaigns/CampaignPerformance';
import { PeriodFilter } from '../dashboard/AnalyticsShell';
import { CampaignCardsGrid } from '../../../features/campaigns/CampaignCards';
import { useSession } from '../../../auth/useSession';
import type { Intervalo, Periodo, PeriodoId } from '../dashboard/analytics-ui';

/** A frase que ensina o termo. Uma só, usada em dois lugares. */
const O_QUE_E =
  'Uma campanha junta os grupos que vão receber gente sob um mesmo código. É por ela que você compara de onde vieram as entradas.';

type Confirm = { kind: 'archive'; campaign: Campaign } | { kind: 'remove'; campaign: Campaign };

export function CampaignsPage() {
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();
  useDocumentTitle('Campanhas');

  const [includeArchived, setIncludeArchived] = useState(false);
  const campaignsQuery = useCampaigns(workspace.id, includeArchived);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [groupsOf, setGroupsOf] = useState<Campaign | null>(null);
  const [metaOf, setMetaOf] = useState<Campaign | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const create = useCreateCampaign();
  const update = useUpdateCampaign();
  const archive = useArchiveCampaign();
  const unarchive = useUnarchiveCampaign();
  const remove = useRemoveCampaign();
  const linkGroup = useLinkCampaignGroup();
  /** Vínculo em lote em andamento na gaveta de grupos. */
  const [vinculo, setVinculo] = useState<{ feitos: number; total: number } | null>(null);
  const unlinkGroup = useUnlinkCampaignGroup();
  const linkMeta = useLinkCampaignMeta();
  const unlinkMeta = useUnlinkCampaignMeta();

  const canCreate = can(role, 'campaign:create');
  const canUpdate = can(role, 'campaign:update');
  const canArchive = can(role, 'campaign:archive');
  const canDelete = can(role, 'campaign:delete');

  const data = campaignsQuery.data;
  const campaigns = useMemo(() => data?.campaigns ?? [], [data]);
  const limit: CampaignLimit | undefined = data?.limit;
  /* O servidor já respondeu "cabe mais uma?". Aqui só se LÊ a resposta (invariante 4). */
  const limitReached = limit !== undefined && !limit.unlimited && !limit.allowed;

  const openCreate = useCallback(() => {
    setRowError(null);
    setEditing(null);
    setFormOpen(true);
  }, []);
  // "Novo …" da busca (⌘K) chega como `?novo=1`.
  useOpenFromParam('novo', openCreate, canCreate);

  const submitForm = useCallback(
    async (values: { name: string; description: string }) => {
      const description = values.description.length === 0 ? undefined : values.description;
      if (editing) {
        await update.mutateAsync({
          workspaceId: workspace.id,
          campaignId: editing.id,
          name: values.name,
          // String vazia apaga a descrição; `null` é o jeito de dizer isso ao servidor.
          description: description ?? null,
        });
        toast({ title: 'Campanha atualizada', tone: 'success' });
        return;
      }
      const criada = await create.mutateAsync({
        workspaceId: workspace.id,
        name: values.name,
        ...(description === undefined ? {} : { description }),
      });
      toast({
        title: `Campanha criada — código ${criada.code}`,
        description: 'Vincule os grupos que vão receber gente para ela começar a distribuir.',
        tone: 'success',
      });
      // Emenda direto no passo seguinte: campanha sem grupo não distribui nada.
      setGroupsOf(criada);
    },
    [create, editing, toast, update, workspace.id],
  );

  const togglePause = useCallback(
    (campaign: Campaign) => {
      setRowError(null);
      const next = campaign.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      update.mutate(
        { workspaceId: workspace.id, campaignId: campaign.id, status: next },
        {
          onSuccess: (updated) =>
            toast({
              title: updated.status === 'ACTIVE' ? 'Campanha no ar' : 'Campanha pausada',
              description:
                updated.status === 'ACTIVE'
                  ? 'Volta a mandar gente para os grupos vinculados.'
                  : 'Para de mandar gente nova. O histórico continua sendo registrado.',
              tone: 'success',
            }),
          onError: (error: unknown) => setRowError(humanizeError(error)),
        },
      );
    },
    [toast, update, workspace.id],
  );

  const rowProps = {
    timezone: workspace.timezone,
    canUpdate,
    canArchive,
    canDelete,
    onEdit: (campaign: Campaign) => {
      setRowError(null);
      setEditing(campaign);
      setFormOpen(true);
    },
    onGroups: (campaign: Campaign) => {
      setRowError(null);
      setGroupsOf(campaign);
    },
    onMeta: (campaign: Campaign) => {
      setRowError(null);
      setMetaOf(campaign);
    },
    onTogglePause: togglePause,
    onArchive: (campaign: Campaign) => setConfirm({ kind: 'archive', campaign }),
    onRemove: (campaign: Campaign) => setConfirm({ kind: 'remove', campaign }),
    onUnarchive: (campaign: Campaign) => {
      setRowError(null);
      unarchive.mutate(
        { workspaceId: workspace.id, campaignId: campaign.id },
        {
          onSuccess: () =>
            toast({
              title: 'Campanha desarquivada',
              description: 'Voltou pausada — religue quando quiser que ela distribua de novo.',
              tone: 'success',
            }),
          onError: (error: unknown) => setRowError(humanizeError(error)),
        },
      );
    },
  };

  return (
    <>
      <PageHeader
        title="Campanhas"
        description={`${O_QUE_E} Cada uma nasce com um código curto, gerado aqui, que você usa nos seus anúncios.`}
        action={
          canCreate ? (
            <Button
              variant="primary"
              leadingIcon={<Plus />}
              leadingBubble
              block
              disabled={limitReached}
              onClick={openCreate}
            >
              Nova campanha
            </Button>
          ) : null
        }
      />

      {limitReached && limit !== undefined ? (
        <FormAlert tone="info" className="mb-3">
          Seu plano permite {limit.limit} {limit.limit === 1 ? 'campanha' : 'campanhas'} e você já
          está com {limit.current}. Arquive uma campanha ou mude de plano para criar outra.
        </FormAlert>
      ) : null}

      {rowError !== null ? <FormAlert className="mb-3">{rowError}</FormAlert> : null}

      {can(role, 'analytics:read') ? (
        <DesempenhoDasCampanhas
          onVincularGrupo={
            canUpdate
              ? (campaignId) => {
                  const alvo = campaigns.find((c) => c.id === campaignId);
                  if (alvo) {
                    setRowError(null);
                    setGroupsOf(alvo);
                  }
                }
              : undefined
          }
          onVincularMeta={
            canUpdate
              ? () => {
                  // A primeira campanha viva sem ligação com a Meta é a que falta ligar.
                  const alvo =
                    campaigns.find((c) => c.status !== 'ARCHIVED' && !hasMetaLink(c)) ??
                    campaigns.find((c) => c.status !== 'ARCHIVED');
                  if (alvo) {
                    setRowError(null);
                    setMetaOf(alvo);
                  }
                }
              : undefined
          }
        />
      ) : null}

      <Card padded={false}>
        <div className="flex flex-col gap-3 px-4 pt-4 md:flex-row md:items-start md:justify-between md:px-5 md:pt-5">
          <CardHeader
            className="mb-0"
            title="Suas campanhas"
            description="O código é a chave curta da campanha: ele vai nos seus anúncios e não muda nunca, mesmo que você renomeie a campanha depois."
          />

          {(data?.archivedCount ?? 0) > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIncludeArchived((atual) => !atual)}
              className="max-md:w-full"
            >
              {includeArchived
                ? 'Ocultar arquivadas'
                : `Mostrar arquivadas (${data?.archivedCount ?? 0})`}
            </Button>
          ) : null}
        </div>

        {campaignsQuery.isPending ? (
          <div className="mt-4">
            <TableSkeleton rows={3} columns={5} />
          </div>
        ) : campaignsQuery.isError ? (
          <ErrorState
            title="Não conseguimos carregar as campanhas"
            description={humanizeError(campaignsQuery.error)}
            onRetry={() => void campaignsQuery.refetch()}
          />
        ) : campaigns.length === 0 ? (
          <EmptyState
            kind="no-data"
            icon={Megaphone}
            title="Nenhuma campanha ainda"
            description={
              <>
                {O_QUE_E} Crie a primeira, vincule os grupos que vão receber gente, e o MGDLead gera
                o código curto para você usar nos anúncios.
              </>
            }
            action={
              canCreate ? (
                <Button
                  variant="primary"
                  leadingIcon={<Plus />}
                  disabled={limitReached}
                  onClick={openCreate}
                  className="max-md:w-full"
                >
                  Criar a primeira campanha
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            {/* ---------------------------- celular ---------------------------- */}
            <ul className="mt-3 flex flex-col divide-y divide-border border-t border-border md:hidden">
              {campaigns.map((campaign) => (
                <li key={campaign.id}>
                  <CampaignCard campaign={campaign} {...rowProps} />
                </li>
              ))}
            </ul>

            {/* ---------------------------- desktop ---------------------------- */}
            <div className="mt-4 max-md:hidden">
              <Table caption={`Campanhas de ${workspace.name}`}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campanha</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead numeric>Grupos</TableHead>
                    <TableHead>Situação</TableHead>
                    <TableHead>Criada</TableHead>
                    <TableHead sticky="right">
                      <span className="sr-only">Ações</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <CampaignRow key={campaign.id} campaign={campaign} {...rowProps} />
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </Card>

      {/* ------------------------------ diálogos ------------------------------ */}

      <CampaignFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
        mode={editing ? 'edit' : 'create'}
        initialName={editing?.name ?? ''}
        initialDescription={editing?.description ?? ''}
        onSubmit={submitForm}
      />

      <CampaignGroupsDrawer
        workspaceId={workspace.id}
        campaign={groupsOf}
        open={groupsOf !== null}
        onOpenChange={(open) => {
          if (!open) setGroupsOf(null);
        }}
        canUpdate={canUpdate}
        pending={linkGroup.isPending || unlinkGroup.isPending}
        progress={vinculo}
        onLinkMany={async (groupIds) => {
          if (groupsOf === null) return;
          setRowError(null);
          const campaignId = groupsOf.id;
          // Um de cada vez: cada vínculo busca o convite do grupo no WhatsApp, e a ordem
          // da fila é a ordem em que entram.
          const { ok, failed } = await runInSequence(
            groupIds,
            (groupId) => linkGroup.mutateAsync({ workspaceId: workspace.id, campaignId, groupId }),
            (feitos, total) => setVinculo({ feitos, total }),
          );
          setVinculo(null);
          if (failed.length > 0) setRowError(humanizeError(failed[0]?.error));
          if (ok.length > 0) {
            toast({
              title: ok.length === 1 ? 'Grupo vinculado' : `${ok.length} grupos vinculados`,
              description:
                failed.length > 0
                  ? `${failed.length} ${failed.length === 1 ? 'não entrou' : 'não entraram'} — veja o aviso na tela.`
                  : undefined,
              tone: failed.length > 0 ? 'warning' : 'success',
            });
          }
        }}
        onUnlink={(groupId) => {
          if (groupsOf === null) return;
          setRowError(null);
          unlinkGroup.mutate(
            { workspaceId: workspace.id, campaignId: groupsOf.id, groupId },
            {
              onSuccess: () =>
                toast({
                  title: 'Grupo desvinculado',
                  description: 'O grupo continua existindo, com todo o histórico dele.',
                  tone: 'success',
                }),
              onError: (error: unknown) => setRowError(humanizeError(error)),
            },
          );
        }}
      />

      <CampaignMetaDrawer
        workspaceId={workspace.id}
        campaign={metaOf}
        open={metaOf !== null}
        onOpenChange={(open) => {
          if (!open) setMetaOf(null);
        }}
        canUpdate={canUpdate}
        pending={linkMeta.isPending || unlinkMeta.isPending}
        onLink={(externalIds, source) => {
          if (metaOf === null) return;
          setRowError(null);
          linkMeta.mutate(
            { workspaceId: workspace.id, campaignId: metaOf.id, externalIds, source },
            {
              onSuccess: () =>
                toast({
                  title:
                    externalIds.length === 1
                      ? 'Campanha da Meta vinculada'
                      : `${externalIds.length} campanhas da Meta vinculadas`,
                  description:
                    'O gasto delas entra no custo por entrada deste bloco, e problemas nos grupos sobem de prioridade quando houver verba passando.',
                  tone: 'success',
                }),
              onError: (error: unknown) => setRowError(humanizeError(error)),
            },
          );
        }}
        onUnlink={(externalId) => {
          if (metaOf === null) return;
          setRowError(null);
          unlinkMeta.mutate(
            { workspaceId: workspace.id, campaignId: metaOf.id, externalId },
            {
              onSuccess: () =>
                toast({
                  title: 'Ligação desfeita',
                  description:
                    'Os cliques continuam gravados — o que saiu foi a declaração de qual anúncio alimenta esta campanha.',
                  tone: 'success',
                }),
              onError: (error: unknown) => setRowError(humanizeError(error)),
            },
          );
        }}
      />

      <Dialog
        open={confirm !== null}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
        title={confirm?.kind === 'remove' ? 'Excluir campanha' : 'Arquivar campanha'}
        description={
          confirm === null
            ? undefined
            : confirm.kind === 'remove'
              ? `“${confirm.campaign.name}” sai do painel de vez. Esta ação não tem como desfazer.`
              : `“${confirm.campaign.name}” sai da operação e para de distribuir. Dá para desarquivar depois.`
        }
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setConfirm(null)}
              disabled={archive.isPending || remove.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={archive.isPending || remove.isPending}
              loadingLabel={confirm?.kind === 'remove' ? 'Excluindo' : 'Arquivando'}
              onClick={() => {
                if (confirm === null) return;
                const alvo = confirm.campaign;
                const mutation = confirm.kind === 'remove' ? remove : archive;
                mutation.mutate(
                  { workspaceId: workspace.id, campaignId: alvo.id },
                  {
                    onSuccess: () => {
                      setConfirm(null);
                      toast({
                        title:
                          confirm.kind === 'remove' ? 'Campanha excluída' : 'Campanha arquivada',
                        tone: 'success',
                      });
                    },
                    onError: (error: unknown) => {
                      setConfirm(null);
                      setRowError(humanizeError(error));
                    },
                  },
                );
              }}
            >
              {confirm?.kind === 'remove' ? 'Excluir' : 'Arquivar'}
            </Button>
          </>
        }
      >
        {/*
          A pergunta real de quem está prestes a clicar é "perco o histórico?". A resposta
          é NÃO — e ela precisa estar aqui, no momento da dúvida.
        */}
        <p className="text-sm text-muted-foreground">
          As entradas e saídas já registradas nos grupos continuam guardadas, e os grupos não são
          tocados. O que sai daqui é a campanha.
        </p>
      </Dialog>
    </>
  );
}

/* ========================================================================== */
/*  Linha e cartão                                                             */
/* ========================================================================== */

interface RowProps {
  campaign: Campaign;
  timezone: string;
  canUpdate: boolean;
  canArchive: boolean;
  canDelete: boolean;
  onEdit: (campaign: Campaign) => void;
  onGroups: (campaign: Campaign) => void;
  onMeta: (campaign: Campaign) => void;
  onTogglePause: (campaign: Campaign) => void;
  onArchive: (campaign: Campaign) => void;
  onRemove: (campaign: Campaign) => void;
  onUnarchive: (campaign: Campaign) => void;
}

/**
 * O código, do jeito que se copia de uma tela para um anúncio: monoespaçado, espaçado, e
 * sem parecer um campo — porque não é editável em lugar nenhum.
 */
function CampaignCode({ code }: { code: string }) {
  return (
    <Badge
      mono
      className="tracking-wider"
      title="Código da campanha. Gerado pelo MGDLead e fixo para sempre."
    >
      {code}
    </Badge>
  );
}

function StatusCell({
  campaign,
  onMeta,
}: {
  campaign: Campaign;
  onMeta: (campaign: Campaign) => void;
}) {
  const spec = CAMPAIGN_STATUS[campaign.status];
  return (
    <span className="flex flex-wrap items-center gap-1">
      <Badge tone={spec.tone} dot title={spec.hint}>
        {spec.label}
      </Badge>
      {/* G3: o semáforo da meta do mês, quando o bloco tem meta. */}
      <CampaignGoalLight campaignId={campaign.id} />
      {/* No ar e sem para onde mandar ninguém: o silêncio aqui custa entradas. */}
      {isDistributingWithoutGroups(campaign) ? (
        <Badge tone="warning" title="Vincule um grupo para a campanha começar a distribuir.">
          Sem grupo
        </Badge>
      ) : null}
      {/*
        O selo que responde a pergunta do dono direto da lista: "quais destas têm anúncio?".
        Sem ele, saber isso custaria abrir a gaveta de cada campanha, uma por uma.
      */}
      {/*
        Um toque no selo abre a gaveta da Meta (09/10): antes ela só abria pelo menu ⋯, e o
        "Desvincular" ficava escondido dois níveis abaixo do lugar onde a pergunta nasce.
      */}
      {hasMetaLink(campaign) ? (
        <button
          type="button"
          className="rounded-full"
          aria-label={`Campanhas da Meta de “${campaign.name}”`}
          onClick={() => onMeta(campaign)}
        >
          <Badge
            tone="info"
            title="Campanhas da Meta que mandam gente para este bloco: o gasto delas entra no custo por entrada. Toque para ver, vincular ou desvincular."
          >
            Meta{campaign.metaCampaignsCount > 1 ? ` ×${campaign.metaCampaignsCount}` : ''}
          </Badge>
        </button>
      ) : null}
    </span>
  );
}

function CampaignRow(props: RowProps) {
  const { campaign, timezone } = props;
  const archived = campaign.archivedAt !== null;

  return (
    <TableRow>
      <TableRowHeader>
        <span className="flex items-center gap-2.5">
          <Avatar name={campaign.name} shape="square" size="md" />
          <TableEntity
            title={campaign.name}
            sub={
              campaign.description !== null ? (
                <span className="font-sans">{campaign.description}</span>
              ) : undefined
            }
          />
        </span>
      </TableRowHeader>
      <TableCell nowrap>
        <CampaignCode code={campaign.code} />
      </TableCell>
      <TableCell nowrap numeric>
        <span className={campaign.groupsCount === 0 ? 'text-muted-foreground' : 'nums'}>
          {groupsSummary(campaign)}
        </span>
      </TableCell>
      <TableCell nowrap>
        <StatusCell campaign={campaign} onMeta={props.onMeta} />
      </TableCell>
      <TableCell nowrap>
        <span title={formatExact(campaign.createdAt, timezone) ?? undefined}>
          {formatRelative(campaign.createdAt)}
        </span>
      </TableCell>
      <TableCell sticky="right">
        <RowActions {...props} archived={archived} />
      </TableCell>
    </TableRow>
  );
}

function CampaignCard(props: RowProps) {
  const { campaign, timezone } = props;
  const archived = campaign.archivedAt !== null;

  return (
    <div className="flex flex-col gap-2.5 px-4 py-3.5">
      <div className="flex items-start gap-2.5">
        <Avatar name={campaign.name} shape="square" size="md" />
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
          <span className="truncate font-medium">{campaign.name}</span>
          <CampaignCode code={campaign.code} />
        </div>
        <StatusCell campaign={campaign} onMeta={props.onMeta} />
      </div>

      <span
        className={`text-2xs ${campaign.groupsCount === 0 ? 'text-warning' : 'text-muted-foreground'}`}
      >
        {groupsSummary(campaign)} · criada {formatRelative(campaign.createdAt)}
        <span className="sr-only"> em {formatExact(campaign.createdAt, timezone) ?? ''}</span>
      </span>

      <RowActions {...props} archived={archived} />
    </div>
  );
}

/**
 * As ações da linha.
 *
 * Esconder por `can(...)` é UX, não segurança: o servidor barra de qualquer jeito
 * (`permissionProcedure`). O que se evita aqui é oferecer um botão que só daria erro.
 *
 * Reparar na ordem: **excluir só existe na campanha arquivada**. É a mesma sequência que o
 * servidor exige, e mostrá-la na campanha ativa seria oferecer um caminho que termina em
 * "arquive antes" — um erro que a própria tela poderia ter evitado.
 */
function RowActions({
  campaign,
  archived,
  canUpdate,
  canArchive,
  canDelete,
  onEdit,
  onGroups,
  onMeta,
  onTogglePause,
  onArchive,
  onRemove,
  onUnarchive,
}: RowProps & { archived: boolean }) {
  const nome = campaign.name;

  /*
   * Padrão do UI kit: UMA ação visível por linha (a que destrava a campanha — sem grupo ela
   * não distribui nada) e o resto no "…". Cinco botões de texto lado a lado escondiam a ação
   * que importa e espremiam as colunas de dado.
   */
  const itens: ActionMenuItem[] = archived
    ? [
        ...(canArchive
          ? [
              {
                id: 'desarquivar',
                label: 'Desarquivar',
                icon: <ArchiveRestore />,
                onSelect: () => onUnarchive(campaign),
              },
            ]
          : []),
        ...(canDelete
          ? [
              {
                id: 'excluir',
                label: 'Excluir',
                icon: <Trash2 />,
                destructive: true,
                separated: canArchive,
                onSelect: () => onRemove(campaign),
              },
            ]
          : []),
      ]
    : [
        /*
         * "Meta Ads" vale para TODO membro, e não só para quem pode editar: "esta campanha tem
         * anúncio em cima?" é a pergunta de quem está de plantão às 2h da manhã, e ela não
         * depende de poder mexer na configuração. A gaveta esconde a escrita por conta própria.
         */
        { id: 'meta', label: 'Meta Ads', icon: <Target />, onSelect: () => onMeta(campaign) },
        ...(canUpdate
          ? [
              { id: 'editar', label: 'Editar', icon: <Pencil />, onSelect: () => onEdit(campaign) },
              {
                id: 'pausar',
                label: campaign.status === 'ACTIVE' ? 'Pausar' : 'Retomar',
                icon: campaign.status === 'ACTIVE' ? <Pause /> : <Play />,
                onSelect: () => onTogglePause(campaign),
              },
            ]
          : []),
        ...(canArchive
          ? [
              {
                id: 'arquivar',
                label: 'Arquivar',
                icon: <Archive />,
                separated: true,
                onSelect: () => onArchive(campaign),
              },
            ]
          : []),
      ];

  return (
    <span className="flex items-center justify-end gap-1">
      {!archived ? (
        <Button
          size="sm"
          leadingIcon={<Users />}
          aria-label={`Grupos de ${nome}`}
          onClick={() => onGroups(campaign)}
        >
          Grupos
        </Button>
      ) : null}
      {itens.length > 0 ? <ActionMenu label={`Ações de ${nome}`} items={itens} /> : null}
    </span>
  );
}

/**
 * O desempenho no TOPO da tela de campanhas (pedido do dono, 06/10): investimento, CPL Meta,
 * CPL real e as taxas do funil — o que ele procurava e não achava. A lista de cadastro fica
 * embaixo: ela é para configurar, isto é para decidir.
 */
type VisaoDasCampanhas = 'cards' | 'arvore';

/** A escolha "Cards | Árvore de custo" fica lembrada POR USUÁRIO neste navegador (G5). */
const chaveDaVisao = (userId: string) => `mgdlead:campanhas-visao:${userId}`;

function lerVisao(userId: string | undefined): VisaoDasCampanhas {
  if (!userId) return 'cards';
  try {
    return localStorage.getItem(chaveDaVisao(userId)) === 'arvore' ? 'arvore' : 'cards';
  } catch {
    return 'cards';
  }
}

function gravarVisao(userId: string | undefined, visao: VisaoDasCampanhas): void {
  if (!userId) return;
  try {
    localStorage.setItem(chaveDaVisao(userId), visao);
  } catch {
    // sem armazenamento: só volta ao padrão na próxima visita
  }
}

function DesempenhoDasCampanhas({
  onVincularGrupo,
  onVincularMeta,
}: {
  onVincularGrupo?: ((campaignId: string) => void) | undefined;
  onVincularMeta?: (() => void) | undefined;
}) {
  const { workspace } = useWorkspace();
  const userId = useSession().user?.id;
  const [visao, setVisao] = useState<VisaoDasCampanhas>(() => lerVisao(userId));
  const [preset, setPreset] = useState<PeriodoId>('HOJE');
  const [intervalo, setIntervalo] = useState<Intervalo | null>(null);
  const periodo: Periodo = intervalo ?? preset;
  const breakdown = useAnalyticsBreakdown(workspace.id, periodo);
  const cards = useCampaignCards(workspace.id, visao === 'cards');
  const href = (id: string) => workspacePath(workspace.slug, `campanhas/${id}`);

  return (
    <section aria-label="Desempenho das campanhas" className="mb-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs
          variant="segmented"
          value={visao}
          onValueChange={(v) => {
            const proxima: VisaoDasCampanhas = v === 'arvore' ? 'arvore' : 'cards';
            setVisao(proxima);
            gravarVisao(userId, proxima);
          }}
        >
          <TabsList label="Como ver as campanhas">
            <TabsTrigger value="cards">Cards</TabsTrigger>
            <TabsTrigger value="arvore">Árvore de custo</TabsTrigger>
          </TabsList>
        </Tabs>
        {visao === 'arvore' ? (
          <PeriodFilter
            value={preset}
            onChange={setPreset}
            intervalo={{ value: intervalo, onChange: setIntervalo }}
            timezone={workspace.timezone}
          />
        ) : (
          <span className="text-xs text-muted-foreground">Hoje, atualizado a cada minuto</span>
        )}
        <MetaFreshness />
      </div>
      {visao === 'cards' ? (
        <CampaignCardsGrid query={cards} campaignHref={href} onVincularGrupo={onVincularGrupo} />
      ) : breakdown.isError ? (
        <FormAlert>{humanizeError(breakdown.error)}</FormAlert>
      ) : (
        <>
          <CampaignTotals rows={breakdown.data?.funil} onVincularMeta={onVincularMeta} />
          <CampaignPerformanceCard
            rows={breakdown.data?.funil}
            carregando={breakdown.isPending}
            campaignHref={href}
          />
        </>
      )}
    </section>
  );
}
